import { useState, useMemo } from 'react'
import { 
  Palette, 
  TrendingUp, 
  Eye, 
  Heart, 
  Share, 
  Coins, 
  Users, 
  Activity,
  BarChart3,
  FileText,
  Image,
  Video,
  Music,
  Sparkles
} from 'lucide-react'
import { useContent } from '@/hooks/useContent'
import { useAnalytics } from '@/hooks/useAnalytics'
import { useTenant } from '@/contexts/TenantContext'
import ContentGrid from '@/components/content/ContentGrid'
import AnalyticsDashboard from '@/components/analytics/AnalyticsDashboard'
import { formatFileSize } from '@/lib/utils'

const CreatorDashboard: React.FC = () => {
  const { currentTenant } = useTenant()
  const { useUserContent } = useContent()
  const { useTenantAnalytics } = useAnalytics()
  const [activeTab, setActiveTab] = useState<'overview' | 'portfolio' | 'analytics'>('overview')

  // Fetch data with React Query
  const { data: contentData, isLoading: contentLoading } = useUserContent()
  const { data: analyticsData, isLoading: analyticsLoading } = useTenantAnalytics(
    currentTenant?.id || '',
    '7d'
  )

  // Memoized computed data
  const creatorStats = useMemo(() => {
    if (!contentData?.data?.data) return null

    const content = contentData.data.data
    const totalSize = content.reduce((sum, item) => sum + (item.metadata?.size || 0), 0)
    const fileTypes = content.reduce((acc, item) => {
      const type = item.metadata?.mimeType || ''
      if (type.startsWith('image/')) acc.images++
      else if (type.startsWith('video/')) acc.videos++
      else if (type.startsWith('audio/')) acc.audio++
      else acc.documents++
      return acc
    }, { images: 0, videos: 0, audio: 0, documents: 0 })

    return {
      totalCreations: content.length,
      totalSize,
      fileTypes,
      publicCreations: content.filter(item => item.metadata?.isPublic).length,
      featuredCreations: content.filter(item => item.metadata?.featured).length
    }
  }, [contentData])

  const analyticsStats = useMemo(() => {
    if (!analyticsData) return null

    return {
      views: analyticsData.views || 0,
      likes: analyticsData.likes || 0,
      shares: analyticsData.shares || 0,
      revenue: analyticsData.revenue || 0
    }
  }, [analyticsData])

  if (contentLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Creator Studio</h1>
        <p className="text-gray-600 mt-1">Share your creativity with the world</p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'portfolio', label: 'Portfolio', icon: Palette },
            { id: 'analytics', label: 'Analytics', icon: TrendingUp }
          ].map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4 mr-2" />
                {tab.label}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Palette className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Creations</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {creatorStats?.totalCreations || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Eye className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Views</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {analyticsStats?.views.toLocaleString() || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Heart className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Likes</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {analyticsStats?.likes.toLocaleString() || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Coins className="h-6 w-6 text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Revenue</p>
                  <p className="text-2xl font-bold text-gray-900">
                    ${analyticsStats?.revenue.toLocaleString() || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Creation Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Creation Types</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Image className="h-5 w-5 text-blue-500 mr-2" />
                    <span className="text-sm font-medium text-gray-700">Images</span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {creatorStats?.fileTypes.images || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Video className="h-5 w-5 text-red-500 mr-2" />
                    <span className="text-sm font-medium text-gray-700">Videos</span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {creatorStats?.fileTypes.videos || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Music className="h-5 w-5 text-green-500 mr-2" />
                    <span className="text-sm font-medium text-gray-700">Audio</span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {creatorStats?.fileTypes.audio || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FileText className="h-5 w-5 text-gray-500 mr-2" />
                    <span className="text-sm font-medium text-gray-700">Documents</span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {creatorStats?.fileTypes.documents || 0}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Portfolio Stats</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Sparkles className="h-5 w-5 text-purple-500 mr-2" />
                    <span className="text-sm font-medium text-gray-700">Public Creations</span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {creatorStats?.publicCreations || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Heart className="h-5 w-5 text-red-500 mr-2" />
                    <span className="text-sm font-medium text-gray-700">Featured</span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {creatorStats?.featuredCreations || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Share className="h-5 w-5 text-blue-500 mr-2" />
                    <span className="text-sm font-medium text-gray-700">Total Shares</span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {analyticsStats?.shares.toLocaleString() || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Creations */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Creations</h3>
            <div className="space-y-3">
              {contentData?.data?.data?.slice(0, 5).map((item, index) => (
                <div key={item.id} className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <Activity className="h-4 w-4 text-purple-600" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {item.metadata?.title || item.originalFilename}
                    </p>
                    <p className="text-xs text-gray-500">
                      Created {new Date(item.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-gray-500">
                    <span>{formatFileSize(item.metadata?.size || 0)}</span>
                    {item.metadata?.isPublic && (
                      <Sparkles className="h-3 w-3 text-purple-500" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Portfolio Tab */}
      {activeTab === 'portfolio' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">Your Portfolio</h2>
            <div className="flex space-x-2">
              <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                Filter
              </button>
              <button className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700">
                Share Creation
              </button>
            </div>
          </div>

          {contentData?.data?.data ? (
            <ContentGrid 
              content={contentData.data.data}
              variant="grid"
              showActions={true}
            />
          ) : (
            <div className="text-center py-12">
              <Palette className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-500">No creations yet. Start sharing your creativity!</p>
            </div>
          )}
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <AnalyticsDashboard tenantId={currentTenant?.id} />
      )}
    </div>
  )
}

export default CreatorDashboard 